import React, { Suspense, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HeaderProvider } from './context/HeaderContext';
import { LanguageSwitcherProvider } from './context/LanguageSwitcherProvider';
import { AppRoutes } from './routes';
import { SEO } from './components/SEO';
import { LoadingIndicator } from './components/shared/LoadingIndicator';
import { CookieBanner } from './components/cookies/CookieBanner';
import { useCookieConsent } from './hooks/useCookieConsent';
import { useQuery } from '@tanstack/react-query';
import { configClient } from './lib/supabase/configClient';
import { AnalyticsRouteTracker } from './components/AnalyticsRouteTracker';
import { sessionTracker } from './lib/analytics/sessionTracker';
import { analyticsService } from './lib/analytics/analyticsService';
import { logger } from './lib/logger';
import { ActiveAlertBanner } from './components/alerts/ActiveAlertBanner';
import { TestButtonsPanel } from './components/TestButtonsPanel';
import { useHotelConfig } from './hooks/useHotelConfig';

function App() {
  const { showBanner, acceptAll, rejectAll, savePreferences, rgpdConfig } = useCookieConsent();
  const { id: hotelId, slug, baseUrl } = useHotelConfig();
  
  logger.debug('App initialization', {
    category: 'Initialization',
    data: {
      hotelId,
      slug,
      baseUrl,
      isDevelopment: import.meta.env.DEV,
      timestamp: new Date().toISOString()
    }
  });
  
  // Load configuration
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: () => {
      logger.debug('Loading hotel config', {
        category: 'Initialization',
        data: {
          hotelId,
          baseUrl,
          timestamp: new Date().toISOString()
        }
      });
      return configClient.getConfig();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // Track app component mount
  useEffect(() => {
    logger.debug('App component mounted', {
      category: 'Initialization',
      data: {
        hotelId,
        baseUrl,
        timestamp: new Date().toISOString()
      }
    });

    // Track app ready event
    analyticsService.trackEvent({
      event_type: 'system',
      event_name: 'app_ready',
      event_data: {
        timestamp: new Date().toISOString(),
        load_time: performance.now(),
        is_pwa: window.matchMedia('(display-mode: standalone)').matches,
        hotel_id: hotelId,
        hotel_slug: slug,
        base_url: baseUrl
      }
    });

    // Increment page view in session tracker
    sessionTracker.incrementPageView();

    return () => {
      // Track app unmount event (though this rarely happens in SPAs)
      analyticsService.trackEvent({
        event_type: 'system',
        event_name: 'app_unmount',
        event_data: {
          timestamp: new Date().toISOString()
        }
      });
    };
  }, [hotelId, baseUrl, slug]);

  if (error) {
    logger.error('Failed to load configuration', {
      category: 'Initialization',
      data: {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      }
    });
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement de la configuration</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <HeaderProvider>
      <LanguageSwitcherProvider>
        <SEO />
        <AnalyticsRouteTracker />
        <Suspense fallback={<LoadingIndicator id="app-suspense-loader" />}>
          <AppRoutes />
        </Suspense>

        {/* Show active alert banner if config is loaded */}
        {config && <ActiveAlertBanner config={config} />}

        {/* Show cookie banner if needed */}
        {showBanner && config && rgpdConfig && (
          <CookieBanner
            config={rgpdConfig}
            appConfig={config}
            onAcceptAll={acceptAll}
            onRejectAll={rejectAll}
            onSavePreferences={savePreferences}
          />
        )}

        {/* Test buttons panel (toggled with Ctrl+Alt+T) */}
        <TestButtonsPanel />
      </LanguageSwitcherProvider>
    </HeaderProvider>
  );
}

export default App;